import { Form, Link } from 'react-router'
import { getUserImgSrc } from '#app/utils/misc.tsx'
import {
	type PermissionString,
	userHasPermission,
	userHasRole,
} from '#app/utils/user.ts'
import { Logo } from '../../atoms/Logo.tsx'
import { InstallAppMenuItem } from '../../molecules/InstallPwa.tsx'

type HeaderUser = {
	name: string | null
	username: string
	image?: { id: string } | null
}

export type StaffLink = { to: string; label: string }

type StaffUser = Parameters<typeof userHasPermission>[0]

/** each staff page, behind the same right its route asks for */
const STAFF_LINKS: Array<
	StaffLink & { needs: PermissionString | { role: string } }
> = [
	{ to: '/statistics', label: 'Statistikk', needs: 'read:ticket:any' },
	{
		to: '/tickets/phone-order',
		label: 'Telefonbestilling',
		needs: 'create:ticket:any',
	},
	{ to: '/ticket-holders', label: 'E-postliste', needs: { role: 'admin' } },
	{ to: '/admin/festivals', label: 'Festivalår', needs: 'update:event:any' },
	{ to: '/admin/print', label: 'Utskrifter', needs: 'update:event:any' },
	{ to: '/admin/social', label: 'Sosiale medium', needs: 'update:event:any' },
	{ to: '/admin/users', label: 'Brukarar', needs: { role: 'admin' } },
	{ to: '/admin/errors', label: 'Feillogg', needs: 'read:user:any' },
	{ to: '/verification', label: 'Billettkontroll', needs: 'read:ticket:any' },
]

/**
 * The staff pages this user may open, for the "Arrangør" part of the menu:
 * all of them for an admin, "Statistikk" and "Billettkontroll" for door
 * staff, none for a buyer.
 */
export function staffLinksFor(user: StaffUser): StaffLink[] {
	return STAFF_LINKS.filter(({ needs }) =>
		typeof needs === 'string'
			? userHasPermission(user, needs)
			: userHasRole(user ?? null, needs.role),
	).map(({ to, label }) => ({ to, label }))
}

/**
 * The top bar: the brand on the left, the account menu (or "Logg inn") on
 * the right. On the front page it floats transparently over the night sky.
 */
/** "Kari Nordmann" → "KN" — for accounts without a photo */
const initials = (name: string) =>
	name
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map((part) => part.charAt(0).toUpperCase())
		.join('')

export function SiteHeader({
	user,
	staffLinks,
	overlay,
}: {
	user: HeaderUser | null | undefined
	/** the staff pages to list under "Arrangør" (see `staffLinksFor`) */
	staffLinks: StaffLink[]
	/** transparent, light-on-dark (the front page hero) */
	overlay: boolean
}) {
	return (
		<header className="site-header" data-overlay={overlay ? '' : undefined}>
			<Logo />
			<div className="site-header-actions">
				{user ? (
					<div className="dropdown dropdown-end">
						<div
							tabIndex={0}
							role="button"
							aria-label={`Meny for ${user.name ?? user.username}`}
							className="site-header-avatar"
						>
							{user.image?.id ? (
								<img
									alt=""
									src={getUserImgSrc(user.image.id)}
									width={40}
									height={40}
								/>
							) : (
								<span className="site-header-initials" aria-hidden="true">
									{initials(user.name ?? user.username)}
								</span>
							)}
						</div>
						<ul
							tabIndex={0}
							className="menu dropdown-content site-header-menu z-[40]"
						>
							<li className="menu-title">{user.name ?? user.username}</li>
							<li>
								<Link to={`/users/${user.username}/tickets`}>
									Billettane mine
								</Link>
							</li>
							<li>
								<Link to="/tickets/checkout">Kassa</Link>
							</li>
							<li>
								<Link to="/settings/profile">Profil og innstillingar</Link>
							</li>
							<li>
								<Link to="/chat">KI-hjelp</Link>
							</li>
							{staffLinks.length > 0 ? (
								<>
									<li className="menu-title">Arrangør</li>
									{staffLinks.map((link) => (
										<li key={link.to}>
											<Link to={link.to}>{link.label}</Link>
										</li>
									))}
								</>
							) : null}
							<li>
								<InstallAppMenuItem />
							</li>
							<li>
								<Form action="/logout" method="POST">
									<button type="submit" className="w-full text-left">
										Logg ut
									</button>
								</Form>
							</li>
						</ul>
					</div>
				) : (
					<Link to="/login" viewTransition className="site-header-login">
						Logg inn
					</Link>
				)}
			</div>
		</header>
	)
}
